import { useState, useCallback } from 'react';
import { Node, Edge, addEdge, Connection, useNodesState, useEdgesState } from 'reactflow';

interface IUseFlowDataReturn {
    nodes: Node[];
    edges: Edge[];
    onNodesChange: any;
    onEdgesChange: any;
    addNode: (nodeType: string, position?: { x: number; y: number }) => void;
    updateNode: (nodeId: string, updates: Partial<Node>) => void;
    deleteNode: (nodeId: string) => void;
    addEdge: (edge: Edge) => void;
    updateEdge: (edgeId: string, updates: Partial<Edge>) => void;
    deleteEdge: (edgeId: string) => void;
    clearFlow: () => void;
}

export const useFlowData = (): IUseFlowDataReturn => {
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);

    const addNode = useCallback((nodeType: string, position?: { x: number; y: number }) => {
        const id = `${nodeType}_${Date.now()}`;
        const defaultPosition = position || { x: 100, y: 100 };
        
        const newNode: Node = {
            id,
            type: nodeType,
            position: defaultPosition,
            data: {
                type: nodeType,
                label: getDefaultLabel(nodeType),
            },
        };

        setNodes((nds) => [...nds, newNode]);
    }, [setNodes]);

    const updateNode = useCallback((nodeId: string, updates: Partial<Node>) => {
        setNodes((nds) =>
            nds.map((node) =>
                node.id === nodeId ? { ...node, ...updates } : node
            )
        );
    }, [setNodes]);

    const deleteNode = useCallback((nodeId: string) => {
        setNodes((nds) => nds.filter((node) => node.id !== nodeId));
        setEdges((eds) => eds.filter((edge) => 
            edge.source !== nodeId && edge.target !== nodeId
        ));
    }, [setNodes, setEdges]);

    const addFlowEdge = useCallback((edge: Edge) => {
        setEdges((eds) => addEdge(edge, eds));
    }, [setEdges]);

    const updateEdge = useCallback((edgeId: string, updates: Partial<Edge>) => {
        setEdges((eds) =>
            eds.map((edge) =>
                edge.id === edgeId ? { ...edge, ...updates } : edge
            )
        );
    }, [setEdges]);

    const deleteEdge = useCallback((edgeId: string) => {
        setEdges((eds) => eds.filter((edge) => edge.id !== edgeId));
    }, [setEdges]);

    const clearFlow = useCallback(() => {
        setNodes([]);
        setEdges([]);
    }, [setNodes, setEdges]);

    return {
        nodes,
        edges,
        onNodesChange,
        onEdgesChange,
        addNode,
        updateNode,
        deleteNode,
        addEdge: addFlowEdge,
        updateEdge,
        deleteEdge,
        clearFlow,
    };
};

const getDefaultLabel = (nodeType: string): string => {
    switch (nodeType) {
        case 'start':
            return 'Start';
        case 'question':
            return 'Question';
        case 'result':
            return 'Result';
        case 'jump':
            return 'Jump';
        default:
            return 'Node';
    }
};
